/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Real-Time Personalization overview", description = "Use live session behavior and warehouse context to personalize website and app experiences in less than a second.", lastUpdatedDate = new Date(1787788800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Real-Time Personalization overview",
        description: "Use live session behavior and warehouse context to personalize website and app experiences in less than a second.",
        lastUpdatedDate: "Thu Aug 27 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Core features",
          "depth": 0,
          "id": "core-features"
        }, {
          "value": "Build audiences that evaluate live",
          "depth": 1,
          "id": "build-audiences-that-evaluate-live"
        }, {
          "value": "Realtime syncs deliver changes as they happen",
          "depth": 1,
          "id": "realtime-syncs-deliver-changes-as-they-happen"
        }, {
          "value": "Monitor a realtime audience",
          "depth": 1,
          "id": "monitor-a-realtime-audience"
        }, {
          "value": "Serve warehouse data to your app",
          "depth": 1,
          "id": "serve-warehouse-data-to-your-app"
        }, {
          "value": "Get started",
          "depth": 0,
          "id": "get-started"
        }, {
          "value": "When to use Real-Time Personalization",
          "depth": 0,
          "id": "when-to-use-real-time-personalization"
        }, {
          "value": "Common use cases",
          "depth": 1,
          "id": "common-use-cases"
        }, {
          "value": "Use Real-Time Personalization with other Hightouch products",
          "depth": 0,
          "id": "use-real-time-personalization-with-other-hightouch-products"
        }, {
          "value": "Real-Time Personalization vs. Customer Studio",
          "depth": 1,
          "id": "real-time-personalization-vs-customer-studio"
        }, {
          "value": "Permissions",
          "depth": 0,
          "id": "permissions"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      ul: "ul",
      li: "li",
      a: "a",
      h3: "h3",
      h4: "h4"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Audience"
            }), _jsx(_components.th, {
              children: "How you'll use this article"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Marketing"
              })
            }), _jsx(_components.td, {
              children: "Understand what Real-Time Personalization does, when to use it, and how it connects to audiences and activation."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Data teams"
              })
            }), _jsx(_components.td, {
              children: "Learn how realtime events, warehouse context, and audience evaluation work together."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Engineering"
              })
            }), _jsx(_components.td, {
              children: "Understand event collection, evaluation architecture, and Personalization API integration options."
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Real-Time Personalization combines live session behavior with customer data from your warehouse so you can tailor website and app experiences as people browse, search, and convert — typically in less than a second."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Real-Time Personalization lets you respond to what someone is doing on your website or app while they're still active. Instead of waiting for a scheduled audience refresh, you can update audience membership, change on-site content, or trigger downstream actions based on live session behavior."
      }), "\n", _jsx(_components.p, {
        children: "It works by combining two types of information:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "What the person is doing right now"
          }), " — live events like page views, searches, clicks, and cart actions, streamed from ", _jsx(_components.a, {
            href: "/events/overview",
            children: "Hightouch Events"
          }), " or another supported event source."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "What you already know about them"
          }), " — customer attributes from your warehouse, such as loyalty tier, past purchases, or predicted scores."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "When an event arrives, Hightouch evaluates whether the person qualifies for a realtime audience based on rules you define — for example, \"viewed the pricing page 3 times in the last 10 minutes and is on the free plan.\" If they qualify, you can deliver a personalized experience immediately through a realtime sync or the Personalization API."
      }), "\n", _jsx(_components.p, {
        children: "This shifts the question from \"who qualified for this audience today?\" to \"does this person qualify right now?\""
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "core-features",
        children: "Core features"
      }), "\n", _jsx(_components.h3, {
        id: "build-audiences-that-evaluate-live",
        children: "Build audiences that evaluate live"
      }), "\n", _jsxs(_components.p, {
        children: ["Realtime audiences evaluate membership using live session events combined with warehouse attributes. You build them in ", _jsx(_components.a, {
          href: "/customer-studio/overview",
          children: "Customer Studio"
        }), " using the same audience builder you use for standard audiences, but you add conditions that reference a realtime event model. When you turn on ", _jsx(_components.strong, {
          children: "Realtime evaluation"
        }), ", Hightouch evaluates the audience continuously as events arrive instead of on a schedule."]
      }), "\n", _jsx(_components.p, {
        children: "For example, you can build a realtime audience that includes people who searched for a product during their current session and have a lifetime value above a certain threshold. When someone meets those conditions, they enter the audience immediately. When they no longer meet them, they exit — including when a time-based condition expires, so users age out of the audience once the qualifying behavior falls outside its lookback window."
      }), "\n", _jsxs(_components.p, {
        children: ["When an audience combines realtime events with warehouse-backed conditions, Hightouch evaluates the warehouse side against a cache that refreshes on a schedule you control. You can see the cache's freshness on the audience and trigger an on-demand refresh when needed. See ", _jsx(_components.a, {
          href: "/real-time/setup#refresh-warehouse-backed-conditions",
          children: "Refresh warehouse-backed conditions"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "Realtime audiences still use your existing parent models and schema — the difference is how and when evaluation happens."
      }), "\n", _jsx(_components.h3, {
        id: "realtime-syncs-deliver-changes-as-they-happen",
        children: "Realtime syncs deliver changes as they happen"
      }), "\n", _jsxs(_components.p, {
        children: ["Realtime syncs deliver audience membership changes to supported destinations as they happen. When a person enters or exits a realtime audience, Hightouch sends that update to the destination immediately. You can ", _jsx(_components.a, {
          href: "/real-time/setup#configure-events",
          children: "choose which membership changes to forward"
        }), " — entry events, exit events, or both — depending on what the downstream system needs."]
      }), "\n", _jsx(_components.p, {
        children: "You can use realtime syncs to trigger messages, suppress promotions after a conversion, or update campaign targeting without waiting for a batch sync to run."
      }), "\n", _jsx(_components.h3, {
        id: "monitor-a-realtime-audience",
        children: "Monitor a realtime audience"
      }), "\n", _jsxs(_components.p, {
        children: ["Each realtime audience has an ", _jsx(_components.strong, {
          children: "Overview"
        }), " tab you can use to verify that events are flowing, the audience is evaluating correctly, and error rates are within acceptable bounds. Check here first when a realtime audience isn't behaving as expected."]
      }), "\n", _jsxs(_components.p, {
        children: ["For the full audience dashboard, including its charts and history, see ", _jsx(_components.a, {
          href: "/customer-studio/audience-overviews",
          children: "Audience dashboards"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "serve-warehouse-data-to-your-app",
        children: "Serve warehouse data to your app"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.a, {
          href: "/real-time/personalization-api",
          children: "Personalization API"
        }), " is a low-latency API that serves warehouse-backed customer data to your website, app, or backend services. You define the dataset with a model and sync, and Hightouch maintains a managed cache that your application can query over HTTP."]
      }), "\n", _jsx(_components.p, {
        children: "Use the Personalization API to retrieve attributes like product recommendations, customer scores, or profile data, then combine that with realtime audience membership to decide what experience to show. For example, you might check whether someone is in a high-intent pricing audience, then fetch plan details and a recommended upgrade path to render a targeted offer."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "get-started",
        children: "Get started"
      }), "\n", _jsx(_components.p, {
        children: "Setting up Real-Time Personalization usually involves four steps: sending live events, defining realtime events in your schema, building an audience with realtime conditions, and choosing how to activate it."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Step"
            }), _jsx(_components.th, {
              children: "What to do"
            }), _jsx(_components.th, {
              children: "Who typically does this"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "1. Connect an event source"
            }), _jsxs(_components.td, {
              children: ["Set up ", _jsx(_components.a, {
                href: "/events/overview",
                children: "Hightouch Events"
              }), " or connect a supported streaming source to send live events to Hightouch."]
            }), _jsxs(_components.td, {
              children: [_jsx(_components.strong, {
                children: "Engineering team"
              }), " for SDK instrumentation, or ", _jsx(_components.strong, {
                children: "data or engineering team"
              }), " for an existing streaming source"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "2. Define realtime events"
            }), _jsxs(_components.td, {
              children: ["In your ", _jsx(_components.a, {
                href: "/customer-studio/schema",
                children: "Customer Studio schema"
              }), ", create a realtime event model that tells Hightouch which events to evaluate, which fields to expose, and how the event maps back to a user."]
            }), _jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Data or engineering team"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "3. Build a realtime audience"
            }), _jsxs(_components.td, {
              children: ["Create an audience that includes conditions referencing your realtime event model, then turn on ", _jsx(_components.strong, {
                children: "Realtime evaluation"
              }), "."]
            }), _jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Marketing, lifecycle, or data team"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "4. Activate"
            }), _jsxs(_components.td, {
              children: ["Create a ", _jsx(_components.a, {
                href: "/real-time/setup#option-1-realtime-syncs",
                children: "realtime sync"
              }), " to push membership changes to a destination, or connect the ", _jsx(_components.a, {
                href: "/real-time/personalization-api",
                children: "Personalization API"
              }), " for website or app use cases."]
            }), _jsxs(_components.td, {
              children: [_jsx(_components.strong, {
                children: "Marketing ops, lifecycle, or data team"
              }), " for realtime syncs; ", _jsx(_components.strong, {
                children: "engineering team"
              }), " for Personalization API integrations"]
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For the full walkthrough, see ", _jsx(_components.a, {
          href: "/real-time/setup",
          children: "Set up realtime audiences"
        }), "."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Realtime audiences do not currently support traits, experiments, or journeys.\nSee ", _jsx(_components.a, {
            href: "/real-time/architecture#constraints-and-limitations",
            children: "Constraints and\nlimitations"
          }), " for the full\nlist."]
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "when-to-use-real-time-personalization",
        children: "When to use Real-Time Personalization"
      }), "\n", _jsx(_components.p, {
        children: "Use Real-Time Personalization when you need to respond to user behavior during an active session. For scheduled campaign targeting or batch audience updates, standard audiences are usually simpler."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Scenario"
            }), _jsx(_components.th, {
              children: "Recommended approach"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Daily or hourly campaign refresh"
            }), _jsx(_components.td, {
              children: "Standard batch audiences"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Event-triggered messaging, alerts, or operational actions"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/events/event-streaming",
                children: "Event streaming"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Personalization during an active session"
            }), _jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Real-Time Personalization"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Serving warehouse data to your app or website"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/real-time/personalization-api",
                children: "Personalization API"
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "common-use-cases",
        children: "Common use cases"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Suppress promotions after conversion"
          }), " — Remove someone from a promotional audience within seconds of a purchase, so they stop seeing the offer during the same session."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Personalize based on browsing behavior"
          }), " — Show product recommendations or content based on what someone is searching for or viewing right now."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Trigger follow-ups from in-session actions"
          }), " — Send a push notification or update a downstream system when someone abandons checkout or revisits a pricing page."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Combine session context with warehouse data"
          }), " — Build audiences that mix live behavior with historical attributes for more precise targeting."]
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "use-real-time-personalization-with-other-hightouch-products",
        children: "Use Real-Time Personalization with other Hightouch products"
      }), "\n", _jsx(_components.p, {
        children: "Real-Time Personalization connects live session behavior to your warehouse data and your other Hightouch products."
      }), "\n", _jsx(_components.h4, {
        id: "what-real-time-personalization-evaluates",
        children: "What Real-Time Personalization evaluates"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsxs(_components.strong, {
            children: ["Behavioral signals from ", _jsx(_components.a, {
              href: "/events/overview",
              children: "Events"
            })]
          }), " — Real-Time Personalization uses live events such as page views, searches, clicks, and cart actions to understand what someone is doing right now."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsxs(_components.strong, {
            children: ["Customer context from your ", _jsx(_components.a, {
              href: "/sources/overview",
              children: "connected source"
            })]
          }), " — It combines those live signals with customer data from your warehouse, such as loyalty status, past purchases, preferences, and predicted scores."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsxs(_components.strong, {
            children: ["Audiences from ", _jsx(_components.a, {
              href: "/customer-studio/overview",
              children: "Customer Studio"
            })]
          }), " — You define realtime audiences using the same audience builder and schema you use for standard audiences, with the addition of realtime event conditions."]
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "what-real-time-personalization-delivers",
        children: "What Real-Time Personalization delivers"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Personalized website and app experiences"
          }), " — Use realtime audience membership and the Personalization API together to tailor on-site content, offers, and recommendations based on what a person is doing right now and what you already know about them."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsxs(_components.strong, {
            children: ["Audience membership updates to ", _jsx(_components.a, {
              href: "/destinations/overview",
              children: "destinations"
            })]
          }), " — Realtime syncs send audience entry and exit updates to downstream tools for fast activation across channels."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsxs(_components.strong, {
            children: ["Live decision inputs to ", _jsx(_components.a, {
              href: "/ai-decisioning/overview",
              children: "AI Decisioning"
            })]
          }), " — Real-time behavioral signals can help determine which experience or message is most relevant at a given moment."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "real-time-personalization-vs-customer-studio",
        children: "Real-Time Personalization vs. Customer Studio"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.a, {
          href: "/customer-studio/overview",
          children: "Customer Studio"
        }), " and Real-Time Personalization work together, but they handle different parts of the problem:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Customer Studio"
          }), " helps you define audiences and campaign logic using your warehouse data. Standard audiences update on a schedule."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Real-Time Personalization"
          }), " evaluates audience membership from live session events in less than a second, so you can act on behavior while someone is still active."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "You can use them together: build your audience logic in Customer Studio, then turn on Realtime evaluation to deliver responsive experiences when someone qualifies during their session."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "permissions",
        children: "Permissions"
      }), "\n", _jsxs(_components.p, {
        children: ["Real-Time Personalization does not have its own tab in the custom role builder. Realtime audiences use the standard audience and model permissions from Customer Studio, and realtime audience syncs inherit permissions from the associated source and destination grants. To control access, configure grants in the ", _jsx(_components.strong, {
          children: "Customer Studio"
        }), ", ", _jsx(_components.strong, {
          children: "Sources"
        }), ", and ", _jsx(_components.strong, {
          children: "Destinations"
        }), " tabs. See ", _jsx(_components.a, {
          href: "/workspace-management/roles",
          children: "Roles"
        }), " for details."]
      })]
    });
  }
}
export default MDXContent;
